/**
 * Reply vocabulary — the `ANS#` reply records that make every reply Marva sends a Ledger line
 * (MAR-110, owner rule: "show anything that Marva responds to").
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * A REPLY IS NOT A DECISION. Six paths answer a shopper without a gateway decision (SMS model
 * reply and turn cap, STOP/HELP, email reply, websocket-chat templated frames, suitability
 * asks, the live storefront comparison email). They are recorded in the `decisions` table
 * under the SAME partition (`pk = WORKSPACE#{ws}`) but a DIFFERENT sort-key prefix,
 * `ANS#{iso}#{replyId}`, so that nothing that counts decisions can ever count one:
 *
 *   - `'ANS#' < 'DEC#'`, so every `DEC#`-bounded range read stops before them;
 *   - a reply row carries NO GSI key (no `gsi1pk` — CustomerIndex is what the enforcer,
 *     agent-context, the gateway's 5-minute cooldown and the open-loop sweeper read), no
 *     `decisionId`, `mode`, `status`, `trigger`, `action`, `judgment`, no person id, and NO
 *     FREE TEXT — only pointers (`conversationId`, `sessionId`, outbound message id, template
 *     id, suitability pack id). Display text is resolved at read time from stores erasure
 *     already deletes.
 *
 * The caps therefore cannot change: a reply row is invisible to every reader that decides
 * whether to contact a shopper. `packages/shared/__tests__/reply-caps-invariance.test.js`
 * proves it against those readers.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * Dependency-free on purpose (it requires only `./send-hold`, itself require-free): the web
 * app bundles it, and the activity feed loads it. `mintReplyId` uses the platform `crypto.getRandomValues` (Node ≥ 18 and every
 * browser) rather than Node's crypto module.
 *
 * @module reply-vocabulary
 */

'use strict';

const { sendHoldOf, SEND_HOLDS } = require('./send-hold');

/** The sort-key prefix of a reply record. Sorts before `DEC#` — see the module doc. */
const REPLY_SK_PREFIX = 'ANS#';

/** The `kind` every reply record carries; `isReplyRecord` keys on it and on the sk. */
const REPLY_KIND = 'reply';

/** Every reply path that records. Adding one is a vocabulary change, reviewed here. */
const REPLY_SOURCES = Object.freeze([
  'sms_reply', // sms-reply-handler: the model's answer to an inbound text
  'sms_turn_cap', // sms-reply-handler: the long-thread hand-off to chat
  'sms_opt_out', // sms-webhook-handler: STOP confirmation (TwiML)
  'sms_help', // sms-webhook-handler: HELP reply (TwiML)
  'email_reply', // email-reply-handler: the answer to an inbound email
  'chat_template', // websocket-chat: request_help / customer_response / contact_preference
  'chat_send_failure', // websocket-chat: the apology after a failed chat send
  'suitability_ask', // websocket-chat: a suitability pack granted to the shopper
  'suitability_ack', // websocket-chat: a suitability answer acknowledged
  'storefront_comparison' // shopper-email processComparison, live, with no decisionId
]);

/** The channel each source speaks on — derived, never supplied, so it cannot disagree. */
const REPLY_SOURCE_CHANNELS = Object.freeze({
  sms_reply: 'sms',
  sms_turn_cap: 'sms',
  sms_opt_out: 'sms',
  sms_help: 'sms',
  email_reply: 'email',
  chat_template: 'chat',
  chat_send_failure: 'chat',
  suitability_ask: 'chat',
  suitability_ack: 'chat',
  storefront_comparison: 'email'
});

/** websocket-chat's templated frames that record. The connect greeting deliberately does not. */
const CHAT_TEMPLATE_IDS = Object.freeze([
  'request_help',
  'customer_response',
  'contact_preference',
  'send_failure_apology'
]);

/**
 * What actually happened to the send — stated by the producer AFTER it tried.
 *   sent            the channel accepted it synchronously (SES/Twilio id, a socket post)
 *   handed_off      queued to another component; nothing came back yet
 *   unknown         the producer cannot tell — e.g. a STOP/HELP reply returned to Twilio as
 *                   TwiML (Twilio may discard a TwiML body on STOP), or an event whose
 *                   delivery outcome came back ambiguous
 *   failed          the send broke
 *   withheld_shadow shadow mode held it (`send-hold`: every reply path honours shadow since
 *                   MAR-241)
 *   withheld_disabled the workspace is admin-disabled, so nothing went out (F-24 / MAR-24)
 *   withheld_provenance the reply attributed an answer to the team that was not in its context,
 *                   and a rewrite still did (MAR-259) — a limit holding a false claim, so it
 *                   files as `help_blocked`, never as `failed`
 *   withheld_compliance a limit the merchant set (or the law) held the reply before it was
 *                   generated — SMS switched off, the number opted out, the SMS budget spent, or
 *                   quiet hours for a reply that was not immediate (MAR-159). `errorCode` carries
 *                   the reason (`sms-compliance` SKIP_REASONS). A limit working, never `failed`
 */
const REPLY_SEND_STATES = Object.freeze([
  'sent', 'handed_off', 'unknown', 'failed', 'withheld_shadow', 'withheld_provenance', 'withheld_disabled', 'withheld_compliance'
]);

/**
 * Attributes a reply record must NEVER carry. Decision-shaped ones would let a decision reader
 * mistake it for a decision; person ids and GSI keys would let a per-person reader find it;
 * free text would put shopper content where erasure does not look.
 */
const REPLY_FORBIDDEN_ATTRIBUTES = Object.freeze([
  'decisionId', 'mode', 'status', 'trigger', 'action', 'judgment', 'disposition', 'eligibility',
  'decisionFailure', 'feedbackStatus',
  'customerId', 'visitorId', 'email', 'phone', 'recipient', 'recipientEmail', 'recipientPhone',
  'message', 'body', 'bodyText', 'bodyHtml', 'subject', 'text', 'summary', 'reasoning', 'answer',
  'gsi1pk', 'gsi1sk', 'gsi2pk', 'gsi2sk', 'gsi3pk', 'gsi3sk', 'gsi4pk', 'gsi4sk'
]);

/**
 * The chip a reply line carries when it went out while the workspace was in Shadow. Since
 * MAR-241 every reply path holds in Shadow (`withheld_shadow`), so only rows written before that
 * carry it — kept so their lines stay honest. Worded as history: a present-tense "replies aren't
 * held" would contradict the Authority screen, which (rightly) says they are.
 */
const SHADOW_IGNORED_CHIP = 'Sent before replies were held in Shadow';

const REPLY_ID_PATTERN = /^rep_([0-9a-z]{1,11})_([0-9a-f]{16})$/;

/** Send states in which the reply actually reached the channel. Everything else is an attempt. */
const REPLY_DELIVERED_STATES = Object.freeze(['sent', 'handed_off']);

/** True when a send state means the reply went out (counts toward `sendCount`). */
function isDeliveredSendState(sendState) {
  return REPLY_DELIVERED_STATES.includes(sendState);
}

/**
 * The `workspaceMode` a reply record carries, from a workspace row the producer ALREADY loaded
 * (never a reason to add a read). Built on `send-hold`'s `sendHoldOf`, the one predicate:
 *   'shadow' — the send is held in Shadow (workspace-wide, or the channel is shadowed);
 *   'live'   — `mode === 'live'` and nothing holds the channel;
 *   undefined — anything else (no row, `disabled`, unknown mode): the record says nothing
 *              rather than guess. A disabled workspace's reply says so in its `sendState`.
 *
 * @param {object|null|undefined} workspace
 * @param {'sms'|'email'|'chat'} [channel]
 * @returns {'live'|'shadow'|undefined}
 */
function replyWorkspaceModeOf(workspace, channel) {
  const hold = sendHoldOf(workspace, channel);
  if (hold === SEND_HOLDS.SHADOW) return 'shadow';
  if (hold) return undefined;
  return workspace && workspace.mode === 'live' ? 'live' : undefined;
}

/** True for a reply record (by kind AND sort key — either alone is not enough). */
function isReplyRecord(row) {
  return Boolean(row && typeof row === 'object' && row.kind === REPLY_KIND &&
    typeof row.sk === 'string' && row.sk.startsWith(REPLY_SK_PREFIX));
}

function randomHex16() {
  const bytes = new Uint8Array(8);
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Mint a reply id: `rep_{epochMs base36}_{16 hex}`. The time is IN the id, so the sort key
 * derives from the id alone (`replySkOf`) — the detail route resolves `rep_…` to its row with a
 * single GetItem on `{pk, sk}`, tenant-scoped by the key itself.
 *
 * @param {number} [atMs=Date.now()]
 */
function mintReplyId(atMs = Date.now()) {
  const ms = Math.floor(Number(atMs));
  if (!Number.isFinite(ms) || ms < 0) throw new TypeError('mintReplyId: atMs must be a non-negative epoch ms');
  return `rep_${ms.toString(36)}_${randomHex16()}`;
}

/** The epoch ms a reply id carries, or null for anything that is not a reply id. */
function replyTimeOf(replyId) {
  const m = typeof replyId === 'string' ? REPLY_ID_PATTERN.exec(replyId) : null;
  if (!m) return null;
  const ms = parseInt(m[1], 36);
  return Number.isFinite(ms) ? ms : null;
}

/** `ANS#{iso}#{replyId}`, or null for anything that is not a reply id. */
function replySkOf(replyId) {
  const ms = replyTimeOf(replyId);
  if (ms === null) return null;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return `${REPLY_SK_PREFIX}${d.toISOString()}#${replyId}`;
}

/** True for a string shaped like a reply id. */
function isReplyId(value) {
  return replyTimeOf(value) !== null;
}

/**
 * A reply, projected onto the RECEIPT shape (`decision-receipt.toReceipt`), so the Ledger files
 * and chips it with the same helpers as a decision:
 *   sent → help_executed · handed_off / unknown → help_in_flight · failed → execution_failed ·
 *   withheld_shadow → help_shadowed · withheld_provenance / withheld_disabled / withheld_compliance
 *   → help_blocked (the compliance hold carries its reason as `reasonCode`).
 * A `chat_send_failure` is a failure whatever its apology's send state: it is shown under
 * "all" only, and never as restraint. An unrecognised send state is `help_in_flight` flagged
 * `unrecognisedStatus` (not a contact, filed under no filter). `showsReasoning` is always
 * false — a reply has no reasoning.
 */
function classifyReply(row) {
  const sendState = row && row.sendState;
  const source = row && row.source;
  let cls;
  let unrecognisedStatus = false;
  let failurePhase = null;
  if (source === 'chat_send_failure' || sendState === 'failed') {
    cls = 'execution_failed';
    failurePhase = 'send';
  } else if (sendState === 'sent') {
    cls = 'help_executed';
  } else if (sendState === 'handed_off' || sendState === 'unknown') {
    cls = 'help_in_flight';
  } else if (sendState === 'withheld_shadow') {
    cls = 'help_shadowed';
  } else if (sendState === 'withheld_provenance' || sendState === 'withheld_disabled' || sendState === 'withheld_compliance') {
    cls = 'help_blocked';
  } else {
    cls = 'help_in_flight';
    unrecognisedStatus = true;
  }
  return {
    class: cls,
    reasonCode: sendState === 'withheld_compliance' ? (row.errorCode || null) : null,
    source: 'reply',
    until: null,
    deferredUntil: null,
    failurePhase,
    pendingApproval: false,
    draftFailed: false,
    unrecognisedStatus,
    legacy: false,
    showsReasoning: false
  };
}

// Per source: the done line (`one` / `many`), the `verb` for a failed try, and what it `would`
// have done in Shadow. A pack id is appended where the source takes one. A conversation's
// summary is never appended (MAR-252): it is a first-person paragraph, and a title that carried
// it was a paragraph too. A reply line with a conversation takes that conversation's own Ledger
// line instead (`conversationLedgerLine`, in the activity feed).
const PHRASES = Object.freeze({
  sms_reply: { one: 'Answered a text', many: (n) => `Answered ${n} texts`, would: 'answered a text', verb: 'answer a text' },
  sms_turn_cap: { one: 'Moved a long text thread to chat', many: () => 'Moved a long text thread to chat', would: 'moved a long text thread to chat', verb: 'move a long text thread to chat' },
  sms_opt_out: { one: 'Confirmed a STOP — they won\'t be texted again', many: () => 'Confirmed a STOP — they won\'t be texted again', would: 'confirmed a STOP', verb: 'confirm a STOP' },
  sms_help: { one: 'Sent the HELP reply to a text', many: () => 'Sent the HELP reply to a text', would: 'sent the HELP reply', verb: 'send the HELP reply' },
  email_reply: { one: 'Answered an email', many: (n) => `Answered ${n} emails`, would: 'answered an email', verb: 'answer an email' },
  chat_template: { one: 'Replied in chat', many: (n) => `Replied ${n} times in chat`, would: 'replied in chat', verb: 'reply in chat' },
  chat_send_failure: { one: 'A chat reply didn\'t go through — she apologised', many: () => 'A chat reply didn\'t go through — she apologised', would: 'replied in chat', verb: 'reply in chat' },
  suitability_ask: { one: 'Asked a fit question in chat', many: (n) => `Asked ${n} fit questions in chat`, would: 'asked a fit question', verb: 'ask a fit question' },
  suitability_ack: { one: 'Acknowledged a fit answer in chat', many: (n) => `Acknowledged ${n} fit answers in chat`, would: 'acknowledged a fit answer', verb: 'acknowledge a fit answer' },
  storefront_comparison: { one: 'Emailed a comparison a shopper asked for', many: (n) => `Emailed ${n} comparisons shoppers asked for`, would: 'emailed a comparison a shopper asked for', verb: 'email a comparison a shopper asked for' }
});

/**
 * Why a `withheld_compliance` reply was held, in the merchant's words — keyed on the
 * `sms-compliance` SKIP_REASONS value the sender reported (MAR-159). An unlisted reason reads as
 * "a limit you set held it", never as a failure.
 */
const COMPLIANCE_HOLD_WORDS = Object.freeze({
  quiet_hours: 'it was outside your quiet hours, and not an immediate answer',
  sms_disabled: 'text messages are switched off',
  suppressed: 'they have opted out of texts',
  daily_limit: 'your daily text limit was reached',
  monthly_limit: 'your monthly text limit was reached',
  twilio_not_configured: 'your SMS provider is not connected',
  invalid_phone: 'their number is not one we can text'
});

/**
 * One plain line for a reply (or a group of `turns` replies in one conversation).
 *
 * @param {object} row - a reply record
 * @param {object} [opts]
 * @param {number} [opts.turns=1] - how many replies this line stands for
 * @returns {string}
 */
function summarizeReply(row, { turns = 1 } = {}) {
  const p = PHRASES[row && row.source];
  if (!p) return 'Replied to a shopper';
  const c = classifyReply(row);
  if (row.source === 'chat_send_failure') return p.one;
  if (c.class === 'execution_failed') return `Tried to ${p.verb}, but it didn't go through`;
  if (c.class === 'help_shadowed') return `Would have ${p.would} — held in Shadow`;
  if (row.sendState === 'withheld_disabled') {
    return `Didn't ${p.verb} — this workspace is disabled, so nothing goes out`;
  }
  if (row.sendState === 'withheld_compliance') {
    return `Didn't ${p.verb} — ${COMPLIANCE_HOLD_WORDS[row.errorCode] || 'a limit you set held it'}`;
  }
  if (row.sendState === 'withheld_provenance') {
    return `Didn't ${p.verb} — her draft said the team had answered, and no answer from you was on file`;
  }
  const n = Number.isInteger(turns) && turns > 1 ? turns : 1;
  // The suitability pack key (`style.home_return`, `skin-type`) is an internal id, not words a
  // merchant reads, and no label map for it exists — so the line never carries it (MAR-316).
  // The pack itself stays on the record (`pointers.suitability`).
  return n > 1 ? p.many(n) : p.one;
}

/**
 * The chip a reply line carries in place of the receipt's own: the shadow-ignored chip when the
 * reply went out (sent / handed off) while the workspace was in Shadow — a row from before
 * MAR-241, when these paths did not honour shadow; the line says so rather than pretending it
 * was held. Otherwise null
 * (the caller falls back to the receipt's chip).
 */
function replyChip(row) {
  if (!row || row.workspaceMode !== 'shadow') return null;
  return row.sendState === 'sent' || row.sendState === 'handed_off' ? SHADOW_IGNORED_CHIP : null;
}

/**
 * The conversation a row belongs to — the Ledger groups one conversation's replies into one
 * line by this key (with the filter word). Works on BOTH row kinds:
 *   - a reply record: chat and suitability sources by `sessionId`; SMS and email by
 *     `conversationId`; the storefront comparison belongs to no conversation;
 *   - a decision row: only the chat fast path's `customer_message:chat` row, by its
 *     `triggerContext.sessionId`.
 * Null means "stands alone".
 */
function conversationKeyOf(row) {
  if (!row || typeof row !== 'object') return null;
  if (isReplyRecord(row)) {
    const channel = REPLY_SOURCE_CHANNELS[row.source];
    if (row.source === 'storefront_comparison') return null;
    if (channel === 'chat') return row.sessionId ? `session:${row.sessionId}` : null;
    return row.conversationId ? `conversation:${row.conversationId}` : null;
  }
  if (row.trigger === 'customer_message' && row.channel === 'chat') {
    const sessionId = row.triggerContext && row.triggerContext.sessionId;
    return sessionId ? `session:${sessionId}` : null;
  }
  return null;
}

module.exports = {
  REPLY_SK_PREFIX,
  REPLY_KIND,
  REPLY_SOURCES,
  REPLY_SOURCE_CHANNELS,
  CHAT_TEMPLATE_IDS,
  REPLY_SEND_STATES,
  REPLY_DELIVERED_STATES,
  REPLY_FORBIDDEN_ATTRIBUTES,
  SHADOW_IGNORED_CHIP,
  isReplyRecord,
  isReplyId,
  isDeliveredSendState,
  replyWorkspaceModeOf,
  mintReplyId,
  replyTimeOf,
  replySkOf,
  classifyReply,
  summarizeReply,
  replyChip,
  conversationKeyOf
};
